import { useState, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { signOut } from "@/lib/auth";
import { shouldForcePasswordChange } from "@/lib/passwordChange";
import { ChangePasswordSection } from "@/components/auth/ChangePasswordSection";

// WP4 (audit pwreset-5): the "change your temporary password on first login"
// promise, finally enforced. Renders instead of the routed page until the
// invited org admin has chosen a password. `cleared` covers the window where
// the Firestore flag write lags a rules deploy: once the password is changed in
// this session, the person is let through regardless.
export function ForcePasswordChangeGate({ children }: { children: ReactNode }) {
  const { user, realOrgUser, realRole, isImpersonating } = useAuth();
  const [cleared, setCleared] = useState(false);
  const providerIds = user?.providerData.map((p) => p.providerId) ?? [];
  const force =
    !cleared &&
    shouldForcePasswordChange({
      role: realRole,
      mustChangePassword: realOrgUser?.mustChangePassword,
      providerIds,
      isImpersonating,
    });

  if (!force) return <>{children}</>;

  return (
    <div className="flex min-h-full items-start justify-center bg-sidebar p-6">
      <div className="w-full max-w-md">
        <p className="mb-3 font-heading text-xs font-semibold uppercase tracking-wider text-teal">Welcome to Studio Manager</p>
        <ChangePasswordSection variant="gate" onChanged={() => setCleared(true)} />
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-4 font-body text-xs text-text-muted underline-offset-2 hover:text-text hover:underline"
        >
          Sign out instead
        </button>
      </div>
    </div>
  );
}
